import React, { useEffect, useState } from "react";
import {
  getGlobalWallet,
  getWalletsByType,
  adjustWalletBalance,
  runSettlementPayout,
  batchSettlePendingOrders, // ✅ Imported new function
  getWalletTransactions,
  WalletData,
} from "../../services/wallet.service";
import { AutoBadge, isAutoTx, txLabel, txIsCredit, txDate } from "../../components/wallet/WalletTxBits";
import { Button } from "../../components/common/Button";
import { Card } from "../../components/common/Card";
import { Spinner } from "../../components/common/Spinner";
import { toast } from "react-hot-toast";
import { WalletRequestsPanel } from "./WalletRequestsPanel";
import { Wallet, TrendingUp, TrendingDown, RefreshCw, ChefHat, User, AlertCircle, History } from "lucide-react";

type WalletSectionProps = {
  trigger?: number;
  onAdminEvent?: () => void;
};

export const WalletSection: React.FC<WalletSectionProps> = ({
  trigger,
  onAdminEvent,
}) => {
  const [activeTab, setActiveTab] = useState<'users' | 'kitchens'>('kitchens');
  const [globalWallet, setGlobalWallet] = useState<WalletData | null>(null);
  const [userWallets, setUserWallets] = useState<WalletData[]>([]);
  const [kitchenWallets, setKitchenWallets] = useState<WalletData[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const [historyTxs, setHistoryTxs] = useState<any[]>([]);

  const toggleHistory = async (walletId: string) => {
    if (historyFor === walletId) { setHistoryFor(null); return; }
    setHistoryFor(walletId);
    setHistoryTxs([]);
    setHistoryTxs(await getWalletTransactions(walletId));
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [global, users, kitchens] = await Promise.all([
        getGlobalWallet(),
        getWalletsByType("user"),
        getWalletsByType("kitchen"), // ✅ Fetch Kitchen Wallets
      ]);
      setGlobalWallet(global);
      setUserWallets(users);
      setKitchenWallets(kitchens);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load wallet data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [trigger]);

  // ✅ ACTION: RUN MANUAL SETTLEMENT SCRIPT
  const handleBatchSync = async () => {
    setIsSyncing(true);
    try {
        const result = await batchSettlePendingOrders();
        toast.success(result.message);
        fetchData(); // Refresh balances
    } catch (error) {
        toast.error("Batch settlement failed");
    } finally {
        setIsSyncing(false);
    }
  };

  // ACTION: ADJUST USER BALANCE
  const handleAdjustUser = async (walletId: string, type: "credit" | "debit") => {
    const amountStr = prompt(`Enter amount to ${type.toUpperCase()}:`);
    if (!amountStr) return;
    const amount = parseFloat(amountStr);
    if (isNaN(amount) || amount <= 0) return toast.error("Invalid amount");

    const reason = prompt("Enter reason for adjustment:");
    if (!reason) return;

    setProcessingId(walletId);
    try {
      await adjustWalletBalance(walletId, amount, type, reason);
      toast.success(`Wallet ${type}ed successfully`);
      fetchData();
    } catch (e) {
      toast.error("Adjustment failed");
    } finally {
      setProcessingId(null);
    }
  };

  // ✅ ACTION: PAYOUT TO KITCHEN
  const handleKitchenPayout = async (wallet: WalletData) => {
      const due = wallet.dueBalance || 0;
      if(due <= 0) return toast.error("No pending dues to settle");

      const amountStr = prompt(`Settle Payment for ${wallet.ownerName}\n\nOutstanding: ₹${due}\n\nEnter Amount to Transfer:`, due.toString());
      if(!amountStr) return;
      
      const amount = parseFloat(amountStr);
      if(amount > due) return toast.error("Cannot pay more than due balance");

      setProcessingId(wallet.id);
      try {
          await runSettlementPayout(wallet.id, amount, "Admin Payout via Wallet Manager");
          toast.success("Payout Recorded Successfully!");
          fetchData();
      } catch(e) {
          toast.error("Payout failed");
      } finally {
          setProcessingId(null);
      }
  };

  if (loading && !globalWallet) return <div className="flex justify-center p-10"><Spinner size={40} /></div>;

  return (
    <div className="space-y-8 p-6">
      <WalletRequestsPanel onChange={fetchData} />

      {/* 1. Global Platform Revenue */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 border-l-4 border-indigo-500 bg-gradient-to-br from-indigo-50 to-white shadow-md col-span-1 md:col-span-2">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-gray-500 font-medium text-sm uppercase tracking-wider mb-1">
                Total Platform Revenue
              </h3>
              <h2 className="text-4xl font-bold text-indigo-900">
                ₹{globalWallet?.balance?.toFixed(2) || "0.00"}
              </h2>
              <p className="text-xs text-indigo-600 mt-2 font-medium">
                Last Updated: {globalWallet?.lastUpdated ? new Date(globalWallet.lastUpdated.toDate()).toLocaleString() : "Never"}
              </p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-full text-indigo-600">
              <Wallet size={32} />
            </div>
          </div>
        </Card>

        {/* Sync Button */}
        <Card className="p-6 border border-gray-200 flex flex-col justify-center items-center text-center space-y-4">
          <div className={`p-3 bg-green-100 rounded-full text-green-600 ${isSyncing ? "animate-spin" : ""}`}>
            <RefreshCw size={24} />
          </div>
          <div>
            <h4 className="font-bold text-gray-800">Sync & Settle</h4>
            <p className="text-xs text-gray-500">
              Finds delivered orders not yet credited to kitchens.
            </p>
          </div>
          <Button onClick={handleBatchSync} disabled={isSyncing} variant="secondary" className="w-full text-sm">
            {isSyncing ? "Running..." : "Run Settlement Script"}
          </Button>
        </Card>
      </div>

      {/* 2. Tabs */}
      <div className="flex border-b border-gray-200">
          <button 
            onClick={() => setActiveTab('kitchens')}
            className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'kitchens' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500'}`}
          >
            <ChefHat size={18}/> Kitchen Payouts (Debts)
          </button>
          <button 
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'users' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'}`}
          >
            <User size={18}/> User Wallets
          </button>
      </div>

      {/* 3. List Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500 font-semibold">
              <tr>
                <th className="p-4">Entity</th>
                <th className="p-4">Phone</th>
                <th className="p-4 text-right">
                    {activeTab === 'kitchens' ? "Payable (Due)" : "Wallet Balance"}
                </th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(activeTab === 'kitchens' ? kitchenWallets : userWallets).map((wallet) => (
                <React.Fragment key={wallet.id}>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="p-4 text-sm text-gray-700">
                    <div className="font-bold flex items-center gap-2">
                        {activeTab === 'kitchens' ? <ChefHat size={16} className="text-orange-500"/> : <User size={16} className="text-blue-500"/>}
                        {wallet.ownerName || "Unknown"}
                    </div>
                    <div className="text-xs text-gray-400 font-mono pl-6">{wallet.id.slice(0,8)}...</div>
                  </td>
                  <td className="p-4 text-sm text-gray-500">{wallet.ownerPhone || "-"}</td>
                  
                  {/* Balance Display Logic */}
                  <td className="p-4 text-right">
                    {activeTab === 'kitchens' ? (
                        <span className={`font-mono font-bold ${wallet.dueBalance && wallet.dueBalance > 0 ? "text-red-600" : "text-gray-400"}`}>
                            ₹{wallet.dueBalance?.toFixed(2) || "0.00"}
                        </span>
                    ) : (
                        <span className={`font-mono font-bold ${wallet.balance < 0 ? "text-red-500" : "text-green-600"}`}>
                            ₹{wallet.balance?.toFixed(2) || "0.00"}
                        </span>
                    )}
                  </td>

                  {/* Action Buttons */}
                  <td className="p-4 flex justify-center gap-2">
                    {activeTab === 'kitchens' ? (
                        <Button 
                            size="sm" 
                            disabled={!wallet.dueBalance || wallet.dueBalance <= 0 || processingId === wallet.id}
                            isLoading={processingId === wallet.id}
                            onClick={() => handleKitchenPayout(wallet)}
                            className={wallet.dueBalance && wallet.dueBalance > 0 ? "bg-green-600 hover:bg-green-700" : "bg-gray-200 text-gray-400"}
                        >
                            Mark Paid
                        </Button>
                    ) : (
                        <>
                            <button onClick={() => handleAdjustUser(wallet.id, "credit")} disabled={processingId === wallet.id} className="p-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200">
                                <TrendingUp size={16} />
                            </button>
                            <button onClick={() => handleAdjustUser(wallet.id, "debit")} disabled={processingId === wallet.id} className="p-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200">
                                <TrendingDown size={16} />
                            </button>
                            <button onClick={() => toggleHistory(wallet.id)} title="Wallet history" className="p-1.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200">
                                <History size={16} />
                            </button>
                        </>
                    )}
                  </td>
                </tr>
                {historyFor === wallet.id && (
                  <tr>
                    <td colSpan={4} className="bg-gray-50 px-4 pb-4">
                      {historyTxs.length === 0 ? <p className="text-xs text-gray-400 py-2">No transactions</p> : (
                        <div className="divide-y divide-gray-200 text-xs">
                          {historyTxs.slice(0, 20).map((tx) => (
                            <div key={tx.id} className="py-2 flex justify-between items-start gap-4">
                              <div>
                                <div className="flex items-center gap-2 font-semibold text-gray-800">{txLabel(tx)} <AutoBadge tx={tx} /></div>
                                <div className={isAutoTx(tx) ? "text-indigo-600" : "text-gray-500"}>{tx.description}</div>
                                <div className="text-gray-400 font-mono">
                                  {txDate(tx)}{tx.txnid ? ` • txn ${tx.txnid}` : ""}{tx.mihpayid ? ` • payu ${tx.mihpayid}` : ""}{tx.refundId ? ` • refund ${tx.refundId}` : ""}
                                </div>
                              </div>
                              <div className="text-right whitespace-nowrap">
                                <div className={`font-mono font-bold ${txIsCredit(tx) ? "text-green-600" : "text-red-600"}`}>{txIsCredit(tx) ? "+" : "-"}₹{Number(tx.amount || 0).toFixed(2)}</div>
                                {typeof tx.balanceAfter === "number" && <div className="text-gray-400">bal ₹{tx.balanceAfter.toFixed(2)}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              
              {(activeTab === 'kitchens' ? kitchenWallets : userWallets).length === 0 && (
                  <tr><td colSpan={4} className="p-8 text-center text-gray-400">No data found</td></tr>
              )}
            </tbody>
          </table>
      </div>
    </div>
  );
};